import React from 'react';

const VALUES = [
    { icon: '◉', title: 'Trust',             desc: 'Lasting relationships through honesty, transparency, and responsible business conduct.' },
    { icon: '◉', title: 'Professionalism',   desc: 'High standards through proper documentation, clear communication, and disciplined execution.' },
    { icon: '◉', title: 'Stability',         desc: 'Long-term property value, reliable service, and sustainable client relationships.' },
    { icon: '◉', title: 'Transparency',      desc: 'Fair dealings, clear processes, and accurate information in every transaction.' },
    { icon: '◉', title: 'Value Creation',    desc: 'Improving property performance, occupancy, rental income, and long-term asset value.' },
    { icon: '◉', title: 'Accountability',    desc: 'Taking full ownership of responsibilities and following through on every commitment.' },
    { icon: '◉', title: 'Client Commitment', desc: 'Protecting the interests of owners, investors, tenants, buyers, and sellers equally.' },
    { icon: '◉', title: 'Op. Excellence',    desc: 'Structured systems and proactive coordination to deliver consistent service quality.' },
];

const STATS = [
    { n: '150+', l: 'Properties Managed' },
    { n: '12+',  l: 'Years in UAE' },
    { n: '98%',  l: 'Client Satisfaction' },
    { n: '15+',  l: 'Developer Partners' },
];

const About: React.FC = () => (
    <>
        <style>{`
      /* ── Banner ── */
      .ab-banner {
        background: linear-gradient(160deg, #020018 0%, #040030 60%, #060038 100%);
        padding: 8rem 5rem 5rem;
        position: relative;
        overflow: hidden;
      }
      .ab-banner::after {
        content: ''; position: absolute; inset: 0; pointer-events: none;
        background: radial-gradient(ellipse 60% 70% at 80% 50%, rgba(77,152,144,0.13) 0%, transparent 65%);
      }
      .ab-banner-grid {
        position: absolute; inset: 0; opacity: 0.04;
        background-image:
          linear-gradient(rgba(77,152,144,1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(77,152,144,1) 1px, transparent 1px);
        background-size: 72px 72px;
      }
      .ab-banner-inner { max-width: 1280px; margin: 0 auto; position: relative; z-index: 1; }
      .ab-banner-label {
        font-family: var(--ff-ui);
        font-size: 0.7rem; font-weight: 600;
        letter-spacing: 0.4em; text-transform: uppercase;
        color: var(--teal);
        display: flex; align-items: center; gap: 1rem;
        margin-bottom: 1.6rem;
      }
      .ab-banner-label::before { content: ''; width: 28px; height: 1px; background: var(--teal); }
      .ab-banner-h1 {
        font-family: var(--ff-h);
        font-size: clamp(3rem, 5.5vw, 5rem);
        font-weight: 400; color: #F0EDE8; line-height: 1.06;
        max-width: 680px;
      }
      .ab-banner-h1 em { color: var(--gold); font-style: italic; }

      /* ── Story ── */
      .ab-story {
        background: var(--bg); padding: 7rem 0;
        transition: background var(--transition); position: relative;
      }
      .ab-story::before {
        content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
        background: linear-gradient(90deg, transparent, var(--border-t), transparent);
      }
      .ab-story-inner { max-width: 1280px; margin: 0 auto; padding: 0 5rem; }
      .ab-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 7rem; align-items: start; }

      /* Left column */
      .ab-h2 {
        font-family: var(--ff-h);
        font-size: clamp(2.2rem, 3.5vw, 3.2rem);
        font-weight: 400; color: var(--ink); line-height: 1.1;
        margin-bottom: 2rem;
      }
      .ab-h2 em { color: var(--teal); font-style: italic; }

      .ab-p {
        font-family: var(--ff-b);
        font-size: 1rem;           /* fixed: was 0.83rem → too small */
        font-weight: 400;          /* fixed: was 300 → too thin */
        line-height: 1.85;
        color: var(--ink2);        /* fixed: stronger than before */
        margin-bottom: 1.4rem;
      }

      /* Quote boxes — clean, minimal */
      .ab-quote {
        border-left: 2px solid var(--teal);
        padding: 1.4rem 1.8rem;
        background: var(--teal-pale);
        margin: 1.8rem 0;
        border-radius: 0 var(--r) var(--r) 0;
        transition: background var(--transition);
      }
      .ab-quote-label {
        font-family: var(--ff-ui);
        font-size: 0.68rem; font-weight: 600;
        letter-spacing: 0.3em; text-transform: uppercase;
        color: var(--teal); margin-bottom: 0.7rem;
      }
      .ab-quote-text {
        font-family: var(--ff-h);
        font-size: 1.05rem;        /* was 1rem — fine, but push a touch */
        font-style: italic; font-weight: 400;
        color: var(--ink2);        /* fixed: was ink2 at 0.65 opacity */
        line-height: 1.85;
      }

      /* ── Values ── */
      .ab-vals-label {
        font-family: var(--ff-ui);
        font-size: 0.7rem; font-weight: 600;
        letter-spacing: 0.38em; text-transform: uppercase;
        color: var(--teal);
        display: flex; align-items: center; gap: 1rem;
        margin-bottom: 1.6rem;
      }
      .ab-vals-label::before { content: ''; width: 28px; height: 1px; background: var(--teal); }

      .ab-vals {
        display: grid; grid-template-columns: 1fr 1fr;
        gap: 0;
        border: 1px solid var(--border);
        background: var(--border);
      }
      .ab-val {
        background: var(--surface);
        padding: 1.8rem 1.6rem;
        transition: background 0.3s, transform 0.25s;
        cursor: default;
      }
      .ab-val:hover { background: var(--surface2); transform: translateY(-2px); }

      .ab-val-icon {
        font-size: 1.2rem; color: var(--teal);
        margin-bottom: 0.8rem; display: block;
      }
      .ab-val-title {
        font-family: var(--ff-ui);
        font-size: 0.72rem;        /* fixed: was 0.65 → too small */
        font-weight: 600;
        letter-spacing: 0.12em; text-transform: uppercase;
        color: var(--ink);         /* fixed: full ink, not faded */
        margin-bottom: 0.5rem;
      }
      .ab-val-desc {
        font-family: var(--ff-b);
        font-size: 0.88rem;        /* fixed: was 0.74 → too small */
        font-weight: 400;          /* fixed: was 300 → too thin */
        line-height: 1.7;
        color: var(--ink3);
      }

      /* ── Stats ── */
      .ab-stats { background: var(--navy); }
      .ab-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); }
      .ab-stat {
        padding: 3rem 2rem;
        border-right: 1px solid rgba(77,152,144,0.12);
        text-align: center;
        transition: background 0.3s;
      }
      .ab-stat:last-child { border-right: none; }
      .ab-stat:hover { background: rgba(77,152,144,0.06); }
      .ab-stat-n {
        font-family: var(--ff-h);
        font-size: 2.6rem; font-weight: 500;
        color: var(--gold); line-height: 1;
      }
      .ab-stat-l {
        font-family: var(--ff-ui);
        font-size: 0.65rem;        /* fixed: was 0.56 → tiny */
        font-weight: 400;
        letter-spacing: 0.2em; text-transform: uppercase;
        color: rgba(240,237,232,0.5);
        margin-top: 8px;
      }

      /* ── Responsive ── */
      @media (max-width: 900px) {
        .ab-banner     { padding: 7rem 2rem 4rem; }
        .ab-story-inner { padding: 0 2rem; }
        .ab-grid       { grid-template-columns: 1fr; gap: 4rem; }
        .ab-stats-grid { grid-template-columns: 1fr 1fr; }
        .ab-stat:nth-child(2) { border-right: none; }
      }
      @media (max-width: 600px) {
        .ab-vals { grid-template-columns: 1fr; }
        .ab-stat { padding: 2rem 1.5rem; }
        .ab-p    { font-size: 0.96rem; }
        .ab-val-desc { font-size: 0.84rem; }
      }
    `}</style>

        {/* Banner */}
        <section id="about" className="ab-banner">
            <div className="ab-banner-grid" />
            <div className="ab-banner-inner">
                <p className="ab-banner-label">Who We Are</p>
                <h1 className="ab-banner-h1">
                    Built on <em>Trust.</em><br />Driven by Purpose.
                </h1>
            </div>
        </section>

        {/* Story + Values */}
        <section className="ab-story">
            <div className="ab-story-inner">
                <div className="ab-grid">

                    {/* Left */}
                    <div>
                        <p className="sec-label">Our Story</p>
                        <h2 className="ab-h2">A Legacy of <em>Professional Excellence</em></h2>
                        <p className="ab-p">
                            Burj Al Arab Real Estate stands as a trusted and value-driven partner in the UAE
                            property market — built on the principles of professional standards, transparent
                            dealings, and relationships that endure beyond a single transaction.
                        </p>
                        <p className="ab-p">
                            We serve landlords, investors, tenants, buyers, and sellers with equal commitment
                            — simplifying property transactions and enhancing asset performance at every step
                            of the journey.
                        </p>
                        <div className="ab-quote">
                            <p className="ab-quote-label">Vision</p>
                            <p className="ab-quote-text">
                                "To be a trusted and value-driven real estate partner in the UAE, recognized for
                                professional standards, transparent dealings, and long-term client relationships."
                            </p>
                        </div>
                        <div className="ab-quote">
                            <p className="ab-quote-label">Mission</p>
                            <p className="ab-quote-text">
                                "To provide professional real estate solutions that simplify property transactions,
                                enhance asset performance, and create lasting value for all."
                            </p>
                        </div>
                    </div>

                    {/* Right */}
                    <div>
                        <p className="ab-vals-label">Core Values</p>
                        <div className="ab-vals">
                            {VALUES.map(v => (
                                <div key={v.title} className="ab-val">
                                    <span className="ab-val-icon">{v.icon}</span>
                                    <p className="ab-val-title">{v.title}</p>
                                    <p className="ab-val-desc">{v.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </section>

        {/* Stats strip */}
        <div className="ab-stats">
            <div className="ab-stats-grid">
                {STATS.map(s => (
                    <div key={s.l} className="ab-stat">
                        <div className="ab-stat-n">{s.n}</div>
                        <div className="ab-stat-l">{s.l}</div>
                    </div>
                ))}
            </div>
        </div>
    </>
);

export default About;